import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RRDLink } from 'react-router-dom';
import type { ContentPageParsed } from '../../../@types/ContentPage';
import type { ElasticVehicle } from '../../../@types/ElasticVehicle';
import { callbackSearch, clear, facets } from '../../../actions/search';
import { track } from '../../../actions/tracking';
import ProductCard from '../../../components/product-card-v2';
import { QueryFilter } from '../../../helpers';
import { asHtml } from '../../../helpers/markdown';
import useTrackPageView from '../../../hooks/useTrackPageView';
import type { RootState } from '../../../reducers';
import { getLandingPage } from '../../../selectors/content-page';
import { getLandingPageQueryFilter } from '../../../selectors/content-page';
import { GuaranteeEvents } from '../../../shared/tracking';
import { Align } from '../../../styled-components/components/Align';
import { Button } from '../../../styled-components/components/Button';
import Checklist from '../../../styled-components/components/Checklist';
import { Container } from '../../../styled-components/components/Container';
import Content from '../../../styled-components/components/Content';
import { H1, H3 } from '../../../styled-components/components/Heading';
import Hero from '../../../styled-components/components/Hero';
import { IconSearch } from '../../../styled-components/components/Icon';
import { Image } from '../../../styled-components/components/Image';
import { Page, PageSection } from '../../../styled-components/components/Page';
import { ProductGrid, ProductGridItem } from '../../../styled-components/components/ProductGrid';
import { StackLarge } from '../../../styled-components/components/Stack';
import { Text } from '../../../styled-components/components/Text';

interface GuaranteePackageLandingPageProps {
  slug: string;
}

const GuaranteePackageLandingPage = ({ slug }: GuaranteePackageLandingPageProps) => {
  const { t } = useTranslation(['common', 'global', 'guaranteePackage']);
  const dispatch = useDispatch();
  const shouldClearSearch = useSelector<RootState, boolean>((s) => !!s.routing.from);
  const page = useSelector<RootState, ContentPageParsed | null>((s) => getLandingPage(s, slug));
  const queryFilter = useSelector<RootState, string | null>((s) =>
    getLandingPageQueryFilter(s, slug),
  );
  const trackPageViewEvent = useTrackPageView();

  const actions = bindActionCreators(
    { clearSearch: clear, getFacets: facets, trackEvent: track },
    dispatch,
  );

  const [result, setResults] = useState<{ items: ElasticVehicle[] | null; totalHits: number }>({
    items: null,
    totalHits: 0,
  });

  const callback = (response: ElasticVehicle[], totalHits: number) =>
    setResults({ items: response, totalHits });

  useEffect(() => {
    if (shouldClearSearch) {
      actions.clearSearch();
      actions.getFacets();
    }
    if (queryFilter) callbackSearch(new QueryFilter(queryFilter).add('hits', 6), callback);
    trackPageViewEvent();
  }, []);

  const onTrack = (buttonLocation: string) => {
    if (page && page.title) {
      actions.trackEvent(GuaranteeEvents.MANUFACTURER_PACKAGE_PAGE_GO_TO_SEARCH, {
        information: {
          manufacturePackageName: page.title,
          buttonLocation,
        },
      });
    }
  };

  if (!page) return null;

  const searchLink = page.relatedSearch?.slice(page.relatedSearch.indexOf('/sok'));

  return (
    <>
      <Hero
        image={
          page.featuredMedia
            ? {
                src: page.featuredMedia,
                srcLtSm: page.smallFeaturedMedia || undefined,
                alt: page.title,
              }
            : undefined
        }
      >
        <H1>{page.title}</H1>
        {searchLink && (
          <Button
            $tone="primary"
            as={RRDLink}
            to={searchLink}
            title={page.umbrellaSearchTag || ''}
            onClick={() => onTrack('top')}
          >
            <IconSearch />
            {page.umbrellaSearchTag}
          </Button>
        )}
      </Hero>
      <Page>
        <Container>
          {page.logo && (
            <PageSection>
              <Image src={`${page.logo}?w=160`} srcSet={`${page.logo}?w=320 2x`} alt={page.title} />
            </PageSection>
          )}
          {page.content && (
            <PageSection>
              <Content dangerouslySetInnerHTML={{ __html: asHtml(page.content) }} />
            </PageSection>
          )}
          {page.uspList && !!page.uspList.length && (
            <PageSection>
              <Checklist items={page.uspList} />
            </PageSection>
          )}
          {page.bottomContent && (
            <PageSection>
              <Content dangerouslySetInnerHTML={{ __html: asHtml(page.bottomContent) }} />
            </PageSection>
          )}
          {result && result.items && result.items.length && (
            <PageSection>
              <StackLarge>
                <H3 as="h2" $margin="none">
                  {t('vehiclesOnBrand', {
                    ns: 'guaranteePackage',
                    title: page.title,
                    brandName: t('brandName', { ns: 'global' }),
                  })}
                </H3>
                <Text $tone="alt" $size="small">
                  {t('numberOfActiveAdsOnBrand', {
                    ns: 'guaranteePackage',
                    count: result.totalHits,
                    brandName: t('brandName', { ns: 'global' }),
                  })}
                </Text>
              </StackLarge>
              <StackLarge>
                <ProductGrid>
                  {result.items.map((item) => (
                    <ProductGridItem key={item.id}>
                      <ProductCard key={item.id} item={item} />
                    </ProductGridItem>
                  ))}
                </ProductGrid>
              </StackLarge>
              {searchLink && (
                <StackLarge>
                  <Align $align="center">
                    <Button
                      $tone="primary"
                      as={RRDLink}
                      to={searchLink}
                      title={page.umbrellaSearchTag || ''}
                      onClick={() => onTrack('bottom')}
                    >
                      <IconSearch />
                      {page.umbrellaSearchTag}
                    </Button>
                  </Align>
                </StackLarge>
              )}
            </PageSection>
          )}
        </Container>
      </Page>
    </>
  );
};

export default GuaranteePackageLandingPage;
